import { camelToKebab } from './camelToKebab';

/**
 * Converts a nested object structure into a flat object of CSS variables.
 * Each property of the input object is transformed into a CSS variable declaration.
 * Nested objects are recursively processed to generate prefixed CSS variable names.
 * @param obj The input object containing nested properties.
 * @param variablePrefix Optional prefix to prepend to CSS variable names (default is 'hc-').
 * @param valueSuffix Optional suffix to append to the CSS variable values.
 * @returns An object where keys are CSS variable names (prefixed with '--') and values are CSS variable values.
 */
export function objectToCssVariables(obj: object, variablePrefix = 'hc-', valueSuffix = ''): Record<string, string> {
    const entries: Array<[string, string]> = [];

    Object.entries(obj).forEach(([key, value]) => {
        const kebabKey = camelToKebab(key);
        const propertyName = `${variablePrefix}${kebabKey}`;

        if (typeof value === 'object' && !Array.isArray(value)) {
            // Recursively handle nested objects
            const nestedCssVariables = objectToCssVariables(value, `${propertyName}-`);
            entries.push(...Object.entries(nestedCssVariables));
        } else {
            // Generate CSS variable declaration
            entries.push([`--${propertyName}`, `${String(value)}${valueSuffix}`]);
        }
    });

    // Convert array of entries into object
    return Object.fromEntries(entries);
}
